import * as Popover from '@radix-ui/react-popover'
import { useEffect, useId, useLayoutEffect, useRef, useState, type MouseEvent } from 'react'
import { cn } from '@/common/lib/cn/cn'
import { Button } from '@/common/ui/Form/Button/Button'
import { Input } from '@/common/ui/Form/Input'
import { Icon } from '@/common/ui/Icon'

export type SearchableSelectOption = {
    value: string
    label: string
}

export type SearchableSelectProps = {
    value: string | null | undefined
    onValueChange: (next: string | null) => void
    /** Поиск на сервере: передаётся строка `q` (после debounce). */
    loadOptions: (q: string) => Promise<SearchableSelectOption[]>
    placeholder?: string
    searchPlaceholder?: string
    emptyText?: string
    disabled?: boolean
    debounceMs?: number
    /** Минимальная длина `q` для запроса (0 — запрос и при пустой строке при открытии). */
    minQueryLength?: number
    className?: string
    size?: 'sm' | 'md' | 'lg' | 'xl'
    'aria-label'?: string
    /** Подпись выбранного значения, если известна (иначе показывается `value`). */
    selectedLabel?: string | null
    onBlur?: () => void
}

export const SearchableSelect = ({
    value,
    onValueChange,
    loadOptions,
    placeholder = 'Выберите…',
    searchPlaceholder = 'Поиск…',
    emptyText = 'Ничего не найдено',
    disabled = false,
    debounceMs = 300,
    minQueryLength = 0,
    className,
    size = 'md',
    'aria-label': ariaLabel,
    selectedLabel,
    onBlur,
}: SearchableSelectProps) => {
    const listId = useId()
    const triggerRef = useRef<HTMLButtonElement>(null)
    const [open, setOpen] = useState(false)
    const [search, setSearch] = useState('')
    const [debouncedQ, setDebouncedQ] = useState('')
    const [options, setOptions] = useState<SearchableSelectOption[]>([])
    const [loading, setLoading] = useState(false)
    const [fetchError, setFetchError] = useState<string | null>(null)

    useEffect(() => {
        const t = window.setTimeout(() => setDebouncedQ(search), debounceMs)
        return () => window.clearTimeout(t)
    }, [search, debounceMs])

    useEffect(() => {
        if (!open) {
            return
        }
        const q = debouncedQ.trim()
        if (q.length < minQueryLength) {
            setOptions([])
            setFetchError(null)
            setLoading(false)
            return
        }

        let cancelled = false
        setLoading(true)
        setFetchError(null)

        void (async () => {
            try {
                const next = await loadOptions(q)
                if (!cancelled) {
                    setOptions(next)
                }
            } catch (e) {
                if (!cancelled) {
                    const message = e instanceof Error ? e.message : 'Ошибка загрузки'
                    setFetchError(message)
                    setOptions([])
                }
            } finally {
                if (!cancelled) {
                    setLoading(false)
                }
            }
        })()

        return () => {
            cancelled = true
        }
    }, [open, debouncedQ, loadOptions, minQueryLength])

    const displayLabel =
        selectedLabel != null && selectedLabel !== '' ? selectedLabel : value != null && value !== '' ? value : null

    const [panelWidth, setPanelWidth] = useState<number | undefined>(undefined)
    const wasOpenRef = useRef(false)
    useEffect(() => {
        if (!open) {
            setLoading(false)
        }
        if (wasOpenRef.current && !open) {
            onBlur?.()
        }
        wasOpenRef.current = open
    }, [open, onBlur])

    useLayoutEffect(() => {
        if (!open) {
            return
        }
        setSearch('')
        setDebouncedQ('')
    }, [open])

    useLayoutEffect(() => {
        if (!open || !triggerRef.current) {
            return
        }
        setPanelWidth(triggerRef.current.offsetWidth)
    }, [open])

    const handleSelect = (opt: SearchableSelectOption) => {
        onValueChange(opt.value)
        setOpen(false)
    }

    const handleClear = (e: MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        onValueChange(null)
        onBlur?.()
    }

    return (
        <Popover.Root open={open} onOpenChange={setOpen}>
            <div className={cn('flex w-full items-stretch gap-1', className)}>
                <Popover.Trigger asChild>
                    <Button
                        ref={triggerRef}
                        type='button'
                        variant='outline'
                        emphasis='neutral'
                        size={size}
                        disabled={disabled}
                        className={cn(
                            'min-w-0 flex-1 justify-between gap-2 font-normal',
                            !displayLabel && 'text-base-content/50'
                        )}
                        aria-label={ariaLabel}
                        aria-expanded={open}
                        aria-haspopup='listbox'
                    >
                        <span className='min-w-0 flex-1 truncate text-left'>{displayLabel ?? placeholder}</span>
                        <Icon name='arrow-down' size='sm' className={cn('shrink-0 opacity-70', open && 'rotate-180')} />
                    </Button>
                </Popover.Trigger>
                {value != null && value !== '' && !disabled ? (
                    <Button
                        type='button'
                        variant='ghost'
                        emphasis='neutral'
                        size={size}
                        className='shrink-0 px-2'
                        aria-label='Очистить'
                        onClick={handleClear}
                    >
                        <Icon name='cross' size='sm' />
                    </Button>
                ) : null}
            </div>
            <Popover.Portal>
                <Popover.Content
                    sideOffset={6}
                    align='start'
                    collisionPadding={12}
                    className='z-[100] rounded-lg border border-base-300 bg-base-100 p-2 shadow-xl outline-none'
                    style={{ width: panelWidth ? `${Math.max(panelWidth, 280)}px` : undefined }}
                    onOpenAutoFocus={e => e.preventDefault()}
                >
                    <Input
                        size={size}
                        type='search'
                        placeholder={searchPlaceholder}
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        className='mb-2 w-full'
                        autoComplete='off'
                        aria-controls={listId}
                    />
                    <div id={listId} role='listbox' className='max-h-60 overflow-y-auto rounded-md' aria-busy={loading}>
                        {fetchError ? (
                            <p className='px-2 py-3 text-sm text-error' role='alert'>
                                {fetchError}
                            </p>
                        ) : loading ? (
                            <div className='flex items-center justify-center gap-2 px-2 py-4 text-sm text-base-content/60'>
                                <span className='loading loading-spinner loading-sm' />
                                Загрузка…
                            </div>
                        ) : options.length === 0 ? (
                            <p className='px-2 py-3 text-sm text-base-content/60'>{emptyText}</p>
                        ) : (
                            <ul className='flex flex-col gap-0.5 p-0'>
                                {options.map(opt => (
                                    <li key={opt.value} role='none'>
                                        <button
                                            type='button'
                                            role='option'
                                            className={cn(
                                                'flex w-full rounded-md px-2 py-2 text-left text-sm hover:bg-base-200',
                                                opt.value === value && 'bg-base-200'
                                            )}
                                            onClick={() => handleSelect(opt)}
                                        >
                                            {opt.label}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </Popover.Content>
            </Popover.Portal>
        </Popover.Root>
    )
}
